'use client';

import { useEffect, useState } from 'react';

/**
 * Which of the article's headings the reader is inside (2026-09-14): the
 * LAST heading whose top is at or above a line just under the fixed header,
 * else the first; the last one once the page is scrolled to its end, so a
 * short closing section can still light up. Observed by id, because the
 * headings are rendered by a server component and carry no refs.
 *
 * The observer's top margin IS that line, so every crossing fires a
 * recompute, and between crossings the answer cannot change. Starts at 0 so
 * the server and the hydrating client agree; nothing reads `window` in render.
 */

/** Just under the 112px scroll margin, so a heading jumped to counts as active. */
const LINE_PX = 128;

export const useActiveHeading = (ids: string[]): number => {
  const [active, setActive] = useState(0);
  const key = ids.join('|');

  useEffect(() => {
    const elements = ids.map(id => document.getElementById(id)).filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const recompute = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

      if (atBottom) {
        setActive(elements.length - 1);

        return;
      }

      let index = 0;

      elements.forEach((el, i) => {
        if (el.getBoundingClientRect().top <= LINE_PX) index = i;
      });

      setActive(index);
    };

    const observer = new IntersectionObserver(recompute, { rootMargin: `-${LINE_PX}px 0px -40% 0px`, threshold: 0 });

    elements.forEach(el => observer.observe(el));
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute);
    };
    // `ids` is rebuilt every render by the caller; the joined key is the identity that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return active;
};
